import {
  getPurchasingPhases,
  getPurchasingWindows,
  useFlag,
  type PurchasingPhase,
  type PurchasingWindow,
  type ScheduledAuction,
  type ScheduledSearchResult,
} from '@b-stock/fe-core/bstock-next/helpers'
import type { OrchestratedAuctionListingDetails } from '@b-stock/fe-core/bstock-next/queries'
import { SoldAs } from '@b-stock/order-api-client'
import { BuyingFormatEnum } from '@b-stock/search-api-client'

import {
  getFirstDibsBoundaries,
  getFirstDibsPurchase,
} from '@/buyer-portal/components/features/listing/hooks/useFirstDibsPurchase'
import { FeatureFlags } from '@/helpers/featureFlags'
import { nextInstant, useRerenderAt } from '@/hooks/useRerenderAt'
import type { SearchResult } from '@/queries/listingSearchQuery'

import soldAsFromBid from './soldAsFromBid'

/** A listing as search returns it, or as the listing page loads it. */
export type PhaseSource = SearchResult | OrchestratedAuctionListingDetails

const getSchedule = (listing: PhaseSource) =>
  'auction3MP' in listing ? listing : listing.auction

/**
 * How the listing sold, when the viewer knows: the order's `soldAs` on the
 * listing page, otherwise the viewer's own bid.
 */
const getSoldAs = (listing: PhaseSource): SoldAs | null => {
  if (!('auction3MP' in listing)) {
    const { auction } = listing
    return listing.soldAs ?? soldAsFromBid(auction.closed, auction.bid)
  }
  const auction = listing.auction3MP
  return auction ? soldAsFromBid(auction.closed, auction.bid) : null
}

type PhaseProps = {
  listing: PhaseSource
  enableFirstDibs: boolean
  now?: number
}

const time = (iso?: string | null) => (iso ? Date.parse(iso) : NaN)

const findWindow = (
  windows: PurchasingWindow[],
  windowType: PurchasingWindow['windowType']
) => windows.find((window) => window.windowType === windowType)

/** Whether the auction has ended: closed, or canceled. */
type AuctionEndFlags = { closed?: boolean; canceled?: boolean }

/** A listing's schedule and its auction's status. */
type PhaseSchedule =
  | (ScheduledAuction & AuctionEndFlags)
  | (Omit<ScheduledSearchResult, 'auction3MP'> & {
      auction3MP: (ScheduledAuction & AuctionEndFlags) | null
    })

/** The phase purchasing is in right now, from the listing's windows alone. */
const getPublicActivePhase = (
  schedule: PhaseSchedule,
  now: number
): PurchasingPhase | null => {
  const { closed, canceled, end } =
    'auction3MP' in schedule
      ? {
          closed: schedule.auction3MP?.closed,
          canceled: schedule.auction3MP?.canceled,
          end: schedule.auction3MP?.actualEndTime ?? schedule.endTime,
        }
      : {
          closed: schedule.closed,
          canceled: schedule.canceled,
          end: schedule.actualEndTime,
        }
  if (closed || canceled) return null

  const windows = getPurchasingWindows(schedule)
  const buyNow = findWindow(windows, 'BUY_NOW')
  const bidding = findWindow(windows, 'BIDDING')
  const inBuyNow = !!buyNow && buyNow.start <= now && now < buyNow.end
  const inBidding = !!bidding && bidding.start <= now && now < time(end)

  if (inBuyNow && inBidding) return 'CBIN'
  if (inBuyNow) return 'BINO'
  if (inBidding) return 'AUCTION'
  return null
}

/** Whether public bidding is open right now, a CBIN phase included. */
export const isBiddingActive = (schedule: PhaseSchedule, now = Date.now()) => {
  const phase = getPublicActivePhase(schedule, now)
  return phase === 'AUCTION' || phase === 'CBIN'
}

/**
 * The phase First Dibs dictates while it applies to this buyer, or null: Buy
 * Now when the group allows early Buy Now, otherwise bidding.
 */
const getFirstDibsPhase = ({
  listing,
  enableFirstDibs,
  now,
}: Required<PhaseProps>): PurchasingPhase | null => {
  const firstDibs = getFirstDibsPurchase({
    listing,
    enabled: enableFirstDibs,
    now,
  })
  if (!firstDibs.fdActive) return null
  // TODO(BP-3236): BOTH is CBIN once the bid card can offer both.
  return firstDibs.fdHasBuyNow ? 'BINO' : 'AUCTION'
}

const soldAsPhase = {
  [SoldAs.Auction]: 'AUCTION',
  [SoldAs.BuyNow]: 'BINO',
} as const

/**
 * The phase purchasing is in right now for this buyer, or null outside it. The
 * buyer's open First Dibs window dictates it; otherwise it is the listing's
 * public phase.
 */
export const getActivePhase = ({
  listing,
  enableFirstDibs,
  now = Date.now(),
}: PhaseProps): PurchasingPhase | null =>
  getFirstDibsPhase({ listing, enableFirstDibs, now }) ??
  getPublicActivePhase(getSchedule(listing), now)

/**
 * The phase to display for this buyer: how it sold, once it has; the phase the
 * buyer's open First Dibs window dictates; otherwise the public phase to
 * display — the first before purchasing, the active one during it and the last
 * after it ends.
 */
export const getDisplayPhase = ({
  listing,
  enableFirstDibs,
  now = Date.now(),
}: PhaseProps): PurchasingPhase => {
  const soldAs = getSoldAs(listing)
  if (soldAs) return soldAsPhase[soldAs]
  const firstDibsPhase = getFirstDibsPhase({ listing, enableFirstDibs, now })
  if (firstDibsPhase) return firstDibsPhase

  const schedule = getSchedule(listing)
  const windows = getPurchasingWindows(schedule)
  const phases = getPurchasingPhases(windows)
  const purchasingStart = Math.min(...windows.map(({ start }) => start))
  const phase =
    now < purchasingStart
      ? phases[0]
      : (getPublicActivePhase(schedule, now) ?? phases[phases.length - 1])
  return phase ?? 'AUCTION'
}

/** The instants at which the listing's public phase changes. */
const getPhaseBoundaries = (listing: PhaseSource) => {
  const schedule = getSchedule(listing)
  const windows = getPurchasingWindows(schedule)
  const actualEnd = Date.parse(
    ('auction3MP' in schedule
      ? (schedule.auction3MP?.actualEndTime ?? schedule.endTime)
      : schedule.actualEndTime) ?? ''
  )
  return [...windows.flatMap(({ start, end }) => [start, end]), actualEnd]
}

/** The phase functions' inputs, re-rendering as any phase boundary passes. */
const usePhaseInputs = (listing: PhaseSource) => {
  const enableFirstDibs = useFlag(FeatureFlags.ENABLE_FIRST_DIBS)
  const now = Date.now() // oxlint-disable-line react/purity
  useRerenderAt(
    nextInstant(
      [...getPhaseBoundaries(listing), ...getFirstDibsBoundaries(listing)],
      now
    )
  )
  return { enableFirstDibs, now }
}

export const useActivePhase = (listing: PhaseSource) =>
  getActivePhase({ listing, ...usePhaseInputs(listing) })

/** `getDisplayPhase` for this buyer, re-rendering as each boundary passes. */
export const useDisplayPhase = (listing: PhaseSource) =>
  getDisplayPhase({ listing, ...usePhaseInputs(listing) })

type BuyingFormats = readonly [BuyingFormatEnum, ...BuyingFormatEnum[]]

/** The buying formats a purchasing phase offers. */
export const getBuyingFormats = (
  phase: PurchasingPhase,
  pricingStrategy: PhaseSource['pricingStrategy'] = 'AUCTION'
): BuyingFormats => {
  if (pricingStrategy === 'MAKE_AN_OFFER') return [BuyingFormatEnum.MakeAnOffer]
  if (phase === 'BINO') return [BuyingFormatEnum.BuyNow]
  if (phase === 'CBIN')
    return [BuyingFormatEnum.Auction, BuyingFormatEnum.BuyNow]
  return [BuyingFormatEnum.Auction]
}

/** The one format to show: Auction whenever it is offered. */
export const getPrimaryBuyingFormat = (formats: BuyingFormats) =>
  formats.includes(BuyingFormatEnum.Auction)
    ? BuyingFormatEnum.Auction
    : formats[0]

/** The buying formats of the phase `getDisplayPhase` displays. */
export const getDisplayBuyingFormats = (props: PhaseProps) =>
  getBuyingFormats(getDisplayPhase(props), props.listing.pricingStrategy)

/** `getDisplayBuyingFormats` for this buyer, re-rendering as each boundary passes. */
export const useDisplayBuyingFormats = (listing: PhaseSource) =>
  getBuyingFormats(useDisplayPhase(listing), listing.pricingStrategy)
